import { useNavigate } from "react-router-dom";
import { useBilling } from "@/context/BillingContext";
import { Lock, ArrowRight, Clock } from "lucide-react";

const TrialExpiredBanner = () => {
  const { isLocked, subscription } = useBilling();
  const navigate = useNavigate();

  // Trial-ending warning: trialing + 3 or fewer days left
  const daysLeft = subscription?.days_left ?? null;
  const isEnding =
    !isLocked &&
    subscription?.status === "trialing" &&
    daysLeft !== null &&
    daysLeft <= 3 &&
    daysLeft > 0;

  if (!isLocked && !isEnding) return null;

  // Soft warning mode (Day 11-13 of 14)
  if (isEnding) {
    const dayLabel = daysLeft === 1 ? "1 day left" : `${daysLeft} days left`;
    return (
      <div
        data-testid="trial-ending-banner"
        className="bg-[#F0EADA] border-b border-[#E4DFD2]"
      >
        <div className="max-w-7xl mx-auto px-8 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-8 h-8 rounded-lg bg-[#B89B5E]/15 flex items-center justify-center flex-none">
              <Clock className="w-4 h-4 text-[#7A5E1F]" strokeWidth={2.25} />
            </div>
            <div className="text-sm">
              <div className="font-semibold text-[#7A5E1F]">
                {dayLabel} on your free trial.
              </div>
              <div className="text-[#69747C] text-xs mt-0.5">
                Pick a plan now to keep editing retainers after your trial ends. You can switch or cancel any time.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/pricing")}
            data-testid="trial-ending-cta"
            className="inline-flex items-center gap-1.5 bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex-none"
          >
            See plans
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Locked / expired / cancelled / past_due — original strong banner
  const isCancelled = subscription?.status === "cancelled" || subscription?.status === "inactive";
  const isPastDue = subscription?.status === "past_due";
  let title;
  let body;
  if (isPastDue) {
    title = "Your last payment didn't go through";
    body = "Update your card in the billing portal to keep your access. We'll keep your data safe in the meantime.";
  } else if (isCancelled) {
    title = "Your plan is paused";
    body = "Re-activate a plan to add or edit retainers. You can still view your data.";
  } else {
    title = "Your free trial has ended";
    body = "Upgrade to keep adding and editing retainers. Your existing data is safe and stays read-only until you choose a plan.";
  }

  return (
    <div
      data-testid="trial-expired-banner"
      className="bg-[#1F2326] text-[#F8F6F1]"
    >
      <div className="max-w-7xl mx-auto px-8 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-8 h-8 rounded-lg bg-[#B89B5E]/20 flex items-center justify-center flex-none">
            <Lock className="w-4 h-4 text-[#B89B5E]" strokeWidth={2.25} />
          </div>
          <div className="text-sm">
            <div className="font-semibold">{title}</div>
            <div className="text-[#C9C2B0] text-xs mt-0.5">{body}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(isPastDue ? "/settings" : "/pricing")}
          data-testid="trial-banner-upgrade-btn"
          className="inline-flex items-center gap-1.5 bg-[#B89B5E] text-[#1F2326] hover:bg-[#A78946] px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex-none"
        >
          {isPastDue ? "Update card" : "Choose a plan"}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default TrialExpiredBanner;
